<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <b-row align-v="center" class="mb-2">
        <b-col>
          <h3 class="mb-0">{{ mode === 'menu' ? 'Menú por sucursal' : 'Simulador de captura' }}</h3>
          <small class="text-muted" v-if="mode === 'menu'">
            Selecciona una de tus sucursales autorizadas para editar los precios de su menú por área.
          </small>
          <small class="text-muted" v-else>
            Selecciona una de tus sucursales autorizadas para abrir la pantalla de captura tal como la
            vería el punto de venta en esa sucursal (productos, grupos, precios por área y extras).
            No se crean cuentas ni tickets reales.
          </small>
        </b-col>
      </b-row>
      <b-row align-v="center">
        <b-col md="6">
          <b-form-group label="Buscar:">
            <b-form-input type="search" v-model="search" debounce="300"
              placeholder="Nombre, código o ID de sucursal"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="auto">
          <b-form-group label=" ">
            <b-form-checkbox v-model="onlyEnabled" switch>Solo habilitadas</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col md="auto">
          <b-form-group label=" ">
            <b-button variant="outline-primary" size="sm" @click="getSucursales" :disabled="loading">
              <b-icon icon="arrow-clockwise"></b-icon> Recargar
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <b-overlay :show="loading" rounded="sm">
        <b-alert :show="!loading && sucursales.length === 0" variant="warning">
          No tienes sucursales autorizadas. Pide a un administrador que te asigne al menos una.
        </b-alert>
        <b-alert v-if="lastSucursal && !loading" show variant="light" class="py-2">
          Última sucursal usada: <b>{{ lastSucursal.nombre }}</b>
          <b-button size="sm" variant="link" class="p-0 ml-2" @click="enter(lastSucursal)">Continuar</b-button>
        </b-alert>
        <b-row>
          <b-col cols="12" sm="6" md="4" lg="3" v-for="sucursal in filteredSucursales" :key="sucursal.id" class="mb-3">
            <b-card class="h-100 sucursal-card" :border-variant="sucursal.status ? 'success' : 'secondary'"
              @click="enter(sucursal)">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h5 class="mb-1">{{ sucursal.nombre }}</h5>
                  <small class="text-muted">ID {{ sucursal.id }}<span v-if="sucursal.code"> · {{ sucursal.code }}</span></small>
                </div>
                <b-icon icon="shop" font-scale="1.5" :variant="sucursal.status ? 'success' : 'secondary'"></b-icon>
              </div>
              <div class="mt-2">
                <b-badge pill v-if="sucursal.status" variant="success">En línea</b-badge>
                <b-badge pill v-else variant="secondary">Desconectada</b-badge>
                <b-badge pill v-if="sucursal.enabled" variant="info" class="ml-1">Habilitada</b-badge>
                <b-badge pill v-else variant="warning" class="ml-1">Deshabilitada</b-badge>
              </div>
              <div class="mt-2 small text-muted" v-if="sucursal.version">POS {{ sucursal.version }}</div>
              <b-button-group size="sm" class="mt-3 w-100">
                <b-button :variant="mode === 'menu' ? 'outline-primary' : 'primary'" @click.stop="enter(sucursal, 'captura')">
                  <b-icon icon="hand-index-thumb"></b-icon> Captura
                </b-button>
                <b-button :variant="mode === 'menu' ? 'primary' : 'outline-primary'" @click.stop="enter(sucursal, 'menu')">
                  <b-icon icon="table"></b-icon> Editar menú
                </b-button>
              </b-button-group>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "@/components/Nav.vue";
import { LAST_SUCURSAL_KEY } from "@/utils/posPreview";

export default {
  name: "PosSucursalSelect",
  components: { Nav },
  data() {
    return {
      loading: false,
      search: "",
      onlyEnabled: true,
      sucursales: [],
      lastSucursal: null,
      // 'captura' (default) o 'menu': define el destino principal al elegir sucursal.
      mode: this.$route.query.mode === "menu" ? "menu" : "captura"
    };
  },
  computed: {
    filteredSucursales() {
      const q = (this.search || "").toString().trim().toLowerCase();
      return this.sucursales
        .filter(s => !this.onlyEnabled || s.enabled)
        .filter(s => {
          if (!q) return true;
          return (
            String(s.id) === q ||
            (s.nombre || "").toLowerCase().includes(q) ||
            (s.code || "").toLowerCase().includes(q)
          );
        })
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
    }
  },
  mounted() {
    this.getSucursales();
  },
  methods: {
    getSucursales() {
      this.loading = true;
      axios
        .get("/sucursals")
        .then(response => {
          this.sucursales = Array.isArray(response.data) ? response.data : [];
          this.loadLast();
        })
        .catch(err => {
          this.$bvToast.toast((err.response && err.response.data) || err.message, {
            title: "No se pudieron cargar las sucursales",
            variant: "danger",
            solid: true
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadLast() {
      try {
        const raw = localStorage.getItem(LAST_SUCURSAL_KEY);
        if (!raw) return;
        const id = parseInt(raw, 10);
        // Solo se ofrece "continuar" si la sucursal sigue autorizada para este usuario.
        this.lastSucursal = this.sucursales.find(s => s.id === id) || null;
      } catch (e) {
        this.lastSucursal = null;
      }
    },
    enter(sucursal, target) {
      try {
        localStorage.setItem(LAST_SUCURSAL_KEY, String(sucursal.id));
      } catch (e) {
        // localStorage no disponible: no es crítico.
      }
      const dest = (target || this.mode) === "menu" ? "/pos/menu/" : "/pos/captura/";
      this.$router.push(dest + sucursal.id).catch(() => {});
    }
  }
};
</script>

<style scoped>
.sucursal-card {
  cursor: pointer;
  transition: box-shadow 0.15s ease-in-out;
}
.sucursal-card:hover {
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.15);
}
</style>
